import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileSpreadsheet, Download, Play, ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';

import { fetchEmployeesAttendance } from '../../store/slices/attendanceSummarySlice';
import {
  buildFilterOptions,
  applyFilters,
  monthRange,
  buildYearOptions,
  MONTHS,
} from '../../utils/attendanceSummaryAnalytics';
import { buildEmployeeReport, buildReportTotals, REPORT_COLUMNS, formatDateTime } from '../../utils/attendanceReportBuilder';
import { generateAttendanceReportExcel, downloadBlob } from '../../utils/attendanceReportExcel';

const today = new Date();

const GROUP_STYLES = {
  Employee: 'bg-gray-100 text-gray-600',
  Days: 'bg-blue-50 text-blue-700',
  Hours: 'bg-emerald-50 text-emerald-700',
  Punctuality: 'bg-amber-50 text-amber-700',
  'Data Quality': 'bg-violet-50 text-violet-700',
};

export default function AttendanceReport({ onBack }) {
  const dispatch = useDispatch();
  const { rows, loading, error } = useSelector((s) => s.attendanceSummary);
  const user = useSelector((s) => s.auth?.user);

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [filters, setFilters] = useState({ location: 'All', grade: 'All', category: 'All', designation: 'All' });

  // Step tracking: has the report been generated? has the file been built?
  const [generated, setGenerated] = useState(false);
  const [generatedRange, setGeneratedRange] = useState(null);
  const [generatedAt, setGeneratedAt] = useState('');
  const [building, setBuilding] = useState(false);
  const [excelFile, setExcelFile] = useState(null); // { blob, filename }
  const [excelError, setExcelError] = useState(null);

  const filterOptions = useMemo(() => buildFilterOptions(rows), [rows]);
  const filteredRows = useMemo(() => applyFilters(rows, filters), [rows, filters]);
  const report = useMemo(
    () => (generated ? buildEmployeeReport(filteredRows, generatedRange) : []),
    [generated, filteredRows, generatedRange]
  );
  const totals = useMemo(() => (report.length ? buildReportTotals(report) : null), [report]);

  const notReadyCount = report.filter((r) => r.payrollReady === 'No').length;

  // ── Step 1: generate the report ──────────────────────────────────────
  const handleGenerate = () => {
    const range = monthRange(year, month);
    setGeneratedRange(range);
    setGeneratedAt(formatDateTime());
    setExcelFile(null);
    setExcelError(null);
    dispatch(fetchEmployeesAttendance({ fromDate: range.from, toDate: range.to }));
    setGenerated(true);
  };

  // Changing any input invalidates the built file.
  const invalidate = () => {
    setExcelFile(null);
    setExcelError(null);
  };

  // ── Step 2: build the Excel file ─────────────────────────────────────
  const handleBuildExcel = async () => {
    setBuilding(true);
    setExcelError(null);
    try {
      const file = generateAttendanceReportExcel({
        report,
        range: generatedRange,
        filters,
        generatedBy: user?.name || user?.serviceNo || '',
      });
      setExcelFile(file);
    } catch (err) {
      console.error('[attendanceReport] Excel generation failed:', err);
      setExcelError(err.message || 'Could not generate the Excel file.');
    } finally {
      setBuilding(false);
    }
  };

  // ── Step 3: download ─────────────────────────────────────────────────
  const handleDownload = () => {
    if (excelFile) downloadBlob(excelFile.blob, excelFile.filename);
  };

  const selectCls =
    'h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700 w-full';

  return (
    <div className="space-y-4">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-900">Attendance Report</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Monthly attendance summary per employee — for payroll input
            </p>
          </div>
        </div>
      </div>

      {/* ── Parameters ──────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <span className="text-[11px] font-medium text-gray-500 mb-1 block">Year</span>
            <select
              value={year}
              onChange={(e) => { setYear(Number(e.target.value)); invalidate(); }}
              className={selectCls}
            >
              {buildYearOptions().map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div>
            <span className="text-[11px] font-medium text-gray-500 mb-1 block">Month</span>
            <select
              value={month}
              onChange={(e) => { setMonth(Number(e.target.value)); invalidate(); }}
              className={selectCls}
            >
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          </div>

          {[
            { key: 'location', label: 'Location', opts: filterOptions.locations },
            { key: 'grade', label: 'Grade', opts: filterOptions.grades },
            { key: 'category', label: 'Category', opts: filterOptions.categories },
            { key: 'designation', label: 'Designation', opts: filterOptions.designations },
          ].map(({ key, label, opts }) => (
            <div key={key}>
              <span className="text-[11px] font-medium text-gray-500 mb-1 block">{label}</span>
              <select
                value={filters[key]}
                onChange={(e) => { setFilters((p) => ({ ...p, [key]: e.target.value })); invalidate(); }}
                className={selectCls}
              >
                <option value="All">All</option>
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
            {loading ? 'Generating...' : 'Generate Report'}
          </button>

          {/* Step 2 appears only once a report is on screen */}
          {generated && !loading && report.length > 0 && (
            <button
              onClick={handleBuildExcel}
              disabled={building}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              {building ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
              {building ? 'Building file...' : 'Generate Excel File'}
            </button>
          )}

          {/* Step 3 appears only once the file exists */}
          {excelFile && (
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              <Download size={15} />
              Download {excelFile.filename}
            </button>
          )}
        </div>
      </div>

      {(error || excelError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error || excelError}
        </div>
      )}

      {/* ── Report ──────────────────────────────────────────────── */}
      {!generated ? (
        <div className="bg-white border border-gray-100 rounded-xl py-16 text-center shadow-sm">
          <FileSpreadsheet size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-sm text-gray-400">Choose a period and click Generate Report</p>
        </div>
      ) : loading ? (
        <div className="bg-white border border-gray-100 rounded-xl py-16 text-center shadow-sm">
          <Loader2 size={28} className="mx-auto text-primary-500 animate-spin mb-3" />
          <p className="text-sm text-gray-400">Loading attendance data...</p>
        </div>
      ) : report.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-xl py-16 text-center shadow-sm">
          <p className="text-sm text-gray-400">No attendance records for this period</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          {/* Report meta */}
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
              Monthly Attendance Summary Report
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Period: {generatedRange?.from} to {generatedRange?.to} · {report.length} employees ·
              Generated {generatedAt}
            </p>
            <p className="text-[11px] text-gray-400 mt-1">
              Based on recorded punches only. Absence, leave and holidays are not included.
            </p>
          </div>

          {notReadyCount > 0 && (
            <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>{notReadyCount}</strong> employee{notReadyCount === 1 ? ' has' : 's have'} single-punch
                days — their work hours are incomplete. Clear these in Attendance Adjustments before using this
                report for salary processing.
              </p>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="text-xs border-collapse min-w-max">
              <thead>
                <tr>
                  {REPORT_COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap border-b border-gray-200 ${
                        GROUP_STYLES[c.group] || 'bg-gray-100 text-gray-600'
                      } ${c.numeric ? 'text-right' : 'text-left'}`}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.map((row, i) => (
                  <tr key={row.serviceNo || i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    {REPORT_COLUMNS.map((c) => (
                      <td
                        key={c.key}
                        className={`px-3 py-2 whitespace-nowrap ${c.numeric ? 'text-right tabular-nums' : 'text-left'} ${
                          c.key === 'name' ? 'font-medium text-gray-900' : 'text-gray-700'
                        } ${c.key === 'payrollReady' && row.payrollReady === 'No' ? 'text-red-600 font-semibold' : ''}`}
                      >
                        {row[c.key] === '' || row[c.key] === null ? '—' : row[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-bold text-gray-900">
                  {REPORT_COLUMNS.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 py-2.5 whitespace-nowrap border-t-2 border-gray-300 ${
                        c.numeric ? 'text-right tabular-nums' : 'text-left'
                      }`}
                    >
                      {totals?.[c.key] === '' || totals?.[c.key] === null || totals?.[c.key] === undefined
                        ? ''
                        : totals[c.key]}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}